import * as React from 'react';
import {
    Checkbox,
    CommandBar,
    DefaultButton,
    DetailsList,
    DetailsListLayoutMode,
    Dialog,
    DialogFooter,
    DialogType,
    Dropdown,
    IDropdownOption,
    IColumn,
    ICommandBarItemProps,
    IconButton,
    Label,
    MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    Selection,
    SelectionMode,
    Separator,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TextField,
} from '@fluentui/react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiHelper } from '../../../ApiHelper';
import {
    AgentTaskDashboardItem,
    AgentTaskDashboardResult,
    DASHBOARD_BUCKETS,
    PageShell,
    SyntheticBanner,
    buildAgencySessionUrl,
    buildAdoPullRequestUrl,
    buildAdoWorkItemUrl,
    buildJobDefinitionUrl,
    formatNumber,
    getJobStatusLabel,
    getPullRequestStatusWithHealthLabel,
    getTaskTypeLabel,
    renderLinkedIdentifier,
    updateSearchParams,
    useCurrentService,
    useDocumentTitle,
} from '../shared';

export function AgentTaskDashboardPage() {
    useDocumentTitle('Task Dashboard');

    const navigate = useNavigate();
    const { serviceName } = useParams();
    const service = useCurrentService(serviceName);
    const [searchParams, setSearchParams] = useSearchParams();
    const bucket = searchParams.get('bucket') ?? 'internal';
    const taskListIdParam = searchParams.get('taskListId');
    const parsedTaskListId = taskListIdParam ? Number(taskListIdParam) : undefined;
    const selectedTaskListId = parsedTaskListId !== undefined
        && Number.isInteger(parsedTaskListId)
        && parsedTaskListId > 0
        ? parsedTaskListId
        : undefined;
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [result, setResult] = React.useState<AgentTaskDashboardResult | null>(null);

    React.useEffect(() => {
        if (!serviceName) {
            return;
        }

        setLoading(true);
        setError(null);

        ApiHelper(`/api/service/${serviceName}/agenttasks/dashboard?bucket=${encodeURIComponent(bucket)}`)
            .then((response: AgentTaskDashboardResult) => {
                setResult(response);
                setLoading(false);
            })
            .catch((err: Error) => {
                setError(err.message || 'Failed to load the task dashboard.');
                setResult(null);
                setLoading(false);
            });
    }, [bucket, serviceName]);

    const taskListOptions = React.useMemo<IDropdownOption[]>(() => {
        const taskLists = new Map<number, AgentTaskDashboardItem>();
        (result?.items ?? []).forEach(item => {
            if (!taskLists.has(item.taskListId)) {
                taskLists.set(item.taskListId, item);
            }
        });

        return [
            { key: 'all', text: 'All task lists' },
            ...Array.from(taskLists.values())
                .sort((left, right) => left.taskListName.localeCompare(right.taskListName)
                    || left.taskListId - right.taskListId)
                .map(item => ({
                    key: String(item.taskListId),
                    text: `${item.taskListName} (#${item.taskListId}) - ${getTaskTypeLabel(item.taskType)}`,
                })),
        ];
    }, [result?.items]);

    const displayedItems = React.useMemo(
        () => selectedTaskListId === undefined
            ? (result?.items ?? [])
            : (result?.items ?? []).filter(item => item.taskListId === selectedTaskListId),
        [result?.items, selectedTaskListId],
    );

    const columns = React.useMemo<IColumn[]>(() => [
        {
            key: 'taskList',
            name: 'Task list',
            minWidth: 180,
            isResizable: true,
            onRender: (item: AgentTaskDashboardItem) => (
                <div>
                    <a
                        href="#"
                        onClick={event => {
                            event.preventDefault();
                            navigate(`/service/${serviceName}/agenttasks/list/${item.taskListId}?snapshotId=${item.snapshotId}&taskId=${item.taskId}`);
                        }}
                    >
                        {item.taskListName}
                    </a>
                    <Text block variant="small">
                        {getTaskTypeLabel(item.taskType)} - #{item.taskListId}
                    </Text>
                </div>
            ),
        },
        {
            key: 'scope',
            name: 'Scope',
            minWidth: 280,
            isResizable: true,
            onRender: (item: AgentTaskDashboardItem) => (
                <a
                    href="#"
                    onClick={event => {
                        event.preventDefault();
                        navigate(`/service/${serviceName}/agenttasks/list/${item.taskListId}?snapshotId=${item.snapshotId}&taskId=${item.taskId}`);
                    }}
                >
                    {item.scope}
                </a>
            ),
        },
        { key: 'jobStatus', name: 'Job status', minWidth: 120, isResizable: true, onRender: (item: AgentTaskDashboardItem) => getJobStatusLabel(item.jobStatus) },
        {
            key: 'pullRequestStatus',
            name: 'PR status',
            minWidth: 160,
            isResizable: true,
            onRender: (item: AgentTaskDashboardItem) => getPullRequestStatusWithHealthLabel(
                item.pullRequestStatus,
                item.pullRequestHealth,
                item.prId !== undefined && item.prId !== null,
            ),
        },
        {
            key: 'agent',
            name: 'Job',
            minWidth: 140,
            isResizable: true,
            onRender: (item: AgentTaskDashboardItem) => (
                <a
                    href={buildAgencySessionUrl(item.agencyJobId) ?? buildJobDefinitionUrl(serviceName, item.jobName)}
                    target={item.agencyJobId ? '_blank' : undefined}
                    rel={item.agencyJobId ? 'noopener noreferrer' : undefined}
                >
                    {item.jobName}
                </a>
            ),
        },
        {
            key: 'ado',
            name: 'ADO',
            minWidth: 100,
            isResizable: true,
            onRender: (item: AgentTaskDashboardItem) => renderLinkedIdentifier(item.adoId, buildAdoWorkItemUrl(service, item.adoId)),
        },
        {
            key: 'pr',
            name: 'PR',
            minWidth: 100,
            isResizable: true,
            onRender: (item: AgentTaskDashboardItem) => renderLinkedIdentifier(item.prId, buildAdoPullRequestUrl(service, item.prId)),
        },
        { key: 'gain', name: 'Gain', minWidth: 90, isResizable: true, onRender: (item: AgentTaskDashboardItem) => formatNumber(item.estimatedGain) },
    ], [navigate, service, serviceName]);

    return (
        <PageShell title="Task Dashboard">
            <SyntheticBanner visible={!!result?.isSynthetic} />

            <Stack horizontal horizontalAlign="space-between" verticalAlign="end" wrap tokens={{ childrenGap: 12 }}>
                <Stack horizontal verticalAlign="end" wrap tokens={{ childrenGap: 12 }}>
                    <div style={{ minWidth: 260 }}>
                        <Dropdown
                            label="Bucket"
                            options={DASHBOARD_BUCKETS}
                            selectedKey={bucket}
                            onChange={(_, option) => updateSearchParams(searchParams, setSearchParams, {
                                bucket: String(option?.key),
                                taskListId: null,
                            })}
                        />
                    </div>
                    <div style={{ minWidth: 320 }}>
                        <Dropdown
                            label="Task list"
                            options={taskListOptions}
                            selectedKey={selectedTaskListId === undefined ? 'all' : String(selectedTaskListId)}
                            onChange={(_, option) => updateSearchParams(searchParams, setSearchParams, {
                                taskListId: option?.key === 'all' ? null : String(option?.key),
                            })}
                        />
                    </div>
                </Stack>
                <DefaultButton text="Back to lists" onClick={() => navigate(`/service/${serviceName}/agenttasks`)} />
            </Stack>

            {error && (
                <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
            )}

            {loading ? (
                <Spinner size={SpinnerSize.large} label="Loading task dashboard..." />
            ) : (
                <DetailsList
                    items={displayedItems}
                    columns={columns}
                    selectionMode={SelectionMode.none}
                    layoutMode={DetailsListLayoutMode.justified}
                />
            )}
        </PageShell>
    );
}
